import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Plus, Trash2, Flag } from "lucide-react";

const KINDS = [
  { v: "note", label: "A note on the bid" },
  { v: "section", label: "A proposal section" },
  { v: "line_item", label: "A line item" },
  { v: "pdf_mark", label: "A mark on the proposal PDF (page)" },
];
export const PROPOSAL_SECTIONS = ["The Options", "Scope and Quantities", "Priced Form", "Responsibility Matrix", "Execution Plan",
  "Site Conditions and How We Planned for Them", "Weather We Planned For", "Billing and Cash Flow Forecast", "Communication and Reporting",
  "Included", "Clarifications and Assumptions", "Excluded", "Commercial Terms"];

const blank = () => ({ kind: "note", ref: "", page: "", text: "" });

// The approver flags what to change; every flag becomes a checklist item the estimator must clear before resubmitting.
export default function ApprovalRejectDialog({ open, onClose, onReject, lineItems = [], busy }) {
  const [flags, setFlags] = useState([blank()]);
  const [comment, setComment] = useState("");

  const close = () => { setFlags([blank()]); setComment(""); onClose(); };
  const upd = (i, patch) => setFlags((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const valid = flags.filter((f) => f.text.trim());
  const submit = () => {
    if (!valid.length) return;
    const out = valid.map((f) => ({
      kind: f.kind, ref: f.ref || null, page: f.kind === "pdf_mark" && f.page ? Number(f.page) : null,
      label: f.kind === "pdf_mark" ? `Proposal PDF${f.page ? ` p.${f.page}` : ""}` : f.ref || KINDS.find((k) => k.v === f.kind).label,
      text: f.text.trim(),
    }));
    onReject({ comment: comment.trim() || out[0].text, flags: out, context: { scope: "general", ref: null, label: null } });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-lg" data-testid="approval-reject-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-4 h-4" /> Request changes</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Flag each thing to change. Every flag is saved with your name, the time and your signature key, and the estimator must clear all of them before resubmitting.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {flags.map((f, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`reject-flag-${i}`}>
              <div className="flex items-center gap-2">
                <Flag className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                <Select value={f.kind} onValueChange={(v) => upd(i, { kind: v, ref: "", page: "" })}>
                  <SelectTrigger data-testid={`reject-flag-kind-${i}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}</SelectContent>
                </Select>
                {flags.length > 1 && <button onClick={() => setFlags((fs) => fs.filter((_, j) => j !== i))} data-testid={`reject-flag-remove-${i}`} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
              {f.kind === "section" && (
                <Select value={f.ref} onValueChange={(v) => upd(i, { ref: v })}>
                  <SelectTrigger data-testid={`reject-flag-section-${i}`} className="h-8 text-xs"><SelectValue placeholder="Which section?" /></SelectTrigger>
                  <SelectContent>{PROPOSAL_SECTIONS.map((s, n) => <SelectItem key={s} value={s}>{n + 1}. {s}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {f.kind === "line_item" && (lineItems.length > 0 ? (
                <Select value={f.ref} onValueChange={(v) => upd(i, { ref: v })}>
                  <SelectTrigger data-testid={`reject-flag-lineitem-${i}`} className="h-8 text-xs"><SelectValue placeholder="Which line item?" /></SelectTrigger>
                  <SelectContent>{lineItems.map((li) => <SelectItem key={li} value={li}>{li}</SelectItem>)}</SelectContent>
                </Select>
              ) : <Input data-testid={`reject-flag-ref-${i}`} className="h-8 text-xs" placeholder="Which line item?" value={f.ref} onChange={(e) => upd(i, { ref: e.target.value })} />)}
              {f.kind === "pdf_mark" && <Input data-testid={`reject-flag-page-${i}`} type="number" min={1} className="h-8 text-xs w-32" placeholder="Page number" value={f.page} onChange={(e) => upd(i, { page: e.target.value })} />}
              <textarea data-testid={`reject-flag-text-${i}`} value={f.text} onChange={(e) => upd(i, { text: e.target.value })} rows={2}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-200" placeholder="What needs to change, and why" />
            </div>
          ))}
          <button onClick={() => setFlags((fs) => [...fs, blank()])} data-testid="reject-add-flag" className="text-xs text-[#003E87] inline-flex items-center gap-1 hover:underline"><Plus className="w-3.5 h-3.5" /> Add another flag</button>
          <div>
            <Label className="text-xs text-slate-500">Summary for the estimator <span className="text-slate-400">(optional)</span></Label>
            <Input data-testid="reject-comment-input" className="mt-1" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="One line that sums it up" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={close}>Cancel</Button>
          <Button data-testid="reject-submit-btn" disabled={!valid.length || busy} onClick={submit} className="bg-rose-600 hover:bg-rose-700 text-white">Send back {valid.length > 1 ? `${valid.length} flags` : "for changes"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
